import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { BRAND, BRAND_SOLID, BRAND_HOVER, BRAND_RED, BRAND_10 } from "@/lib/landing-theme";
import { isValidNationalNumber } from "@/data/phoneCountries";
import PhoneCountryInput from "@/components/PhoneCountryInput";
import { Paperclip, X } from "lucide-react";

/**
 * Melian Dialogue contact form.
 *   • Honeypot field (`md_username_verification_honey`) — silently blocks bots
 *   • Country-code-aware phone regex (dynamic per selected dial code)
 *   • Optional image attachment (max 2MB · jpeg/png/webp only)
 *   • localStorage rate limiter — max 3 submissions per 15 minutes
 *   • POSTs multipart/form-data to /api/contact/send
 */

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const RATE_KEY    = "md_form_hits";
const RATE_MAX    = 3;
const RATE_WINDOW_MS = 15 * 60 * 1000;
const MAX_ATTACHMENT_BYTES = 2 * 1024 * 1024; // 2MB
const ALLOWED_ATTACHMENT_TYPES = ["image/jpeg", "image/png", "image/webp"];

function readHits() {
    try { return JSON.parse(localStorage.getItem(RATE_KEY) || "[]"); }
    catch { return []; }
}
function pruneAndCountHits() {
    const now = Date.now();
    const kept = readHits().filter((t) => now - t < RATE_WINDOW_MS);
    try { localStorage.setItem(RATE_KEY, JSON.stringify(kept)); } catch {}
    return kept.length;
}
function logHit() {
    const hits = readHits();
    hits.push(Date.now());
    try { localStorage.setItem(RATE_KEY, JSON.stringify(hits)); } catch {}
}

export default function ContactForm({ subtleText, faintText, cardBg }) {
    const [status, setStatus]     = useState(null);   // { kind: 'ok'|'err', text: string }
    const [busy,   setBusy]       = useState(false);
    const [dialCode, setDialCode] = useState("+254");
    const [nationalNumber, setNationalNumber] = useState("");
    const [phoneTouched, setPhoneTouched] = useState(false);
    const [attachment, setAttachment] = useState(null); // File | null
    const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState(null);
    const [attachmentError, setAttachmentError] = useState(null);
    const honeypotRef = useRef(null);
    const fileInputRef = useRef(null);

    const onAttachmentChange = (e) => {
        const file = e.target.files?.[0];
        setAttachmentError(null);
        if (!file) { setAttachment(null); return; }
        if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
            setAttachmentError("Only JPEG, PNG, or WEBP images are allowed.");
            e.target.value = "";
            return;
        }
        if (file.size > MAX_ATTACHMENT_BYTES) {
            setAttachmentError("Image must be 2MB or smaller.");
            e.target.value = "";
            return;
        }
        setAttachment(file);
        setAttachmentPreviewUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return URL.createObjectURL(file);
        });
    };

    const removeAttachment = () => {
        setAttachment(null);
        setAttachmentError(null);
        setAttachmentPreviewUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
        });
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setStatus(null);
        setPhoneTouched(true);

        const fd = new FormData(e.currentTarget);
        const name    = String(fd.get("name") || "").trim();
        const email   = String(fd.get("email") || "").trim();
        const message = String(fd.get("message") || "").trim();
        const honey   = String(fd.get("md_username_verification_honey") || "").trim();

        // Rate limit (client-side)
        if (pruneAndCountHits() >= RATE_MAX) {
            setStatus({ kind: "err", text: "Rate limit reached — you've sent 3 messages in the last 15 minutes. Please wait a bit before submitting again." });
            return;
        }

        if (name.length < 2)          return setStatus({ kind: "err", text: "Enter your full name (min 2 characters)." });
        if (!EMAIL_REGEX.test(email)) return setStatus({ kind: "err", text: "Enter a valid email address." });
        if (!isValidNationalNumber(dialCode, nationalNumber)) {
            return setStatus({ kind: "err", text: "Enter a valid phone number for the selected country code." });
        }
        if (message.length < 10)     return setStatus({ kind: "err", text: "Message must be at least 10 characters." });

        setBusy(true);
        try {
            // Fetch the current human-check token (regenerates every UTC hour).
            let humanCheckAnswer = null;
            try {
                const { data } = await api.get("/contact/human-check");
                humanCheckAnswer = data?.token || null;
            } catch { /* if the endpoint is unreachable, fall through — server accepts */ }

            const payload = new FormData();
            payload.append("name", name);
            payload.append("email", email);
            payload.append("phone_dial_code", dialCode);
            payload.append("phone_number", nationalNumber);
            payload.append("message", message);
            payload.append("md_username_verification_honey", honey);
            if (humanCheckAnswer) payload.append("human_check_answer", humanCheckAnswer);
            if (attachment) payload.append("attachment", attachment);

            await api.post("/contact/send", payload, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            logHit();
            setStatus({ kind: "ok", text: "Message received. Our team will reply via your in-app inbox within 24 hours." });
            e.target.reset();
            setNationalNumber("");
            setPhoneTouched(false);
            removeAttachment();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (err?.response?.status === 429) {
                setStatus({ kind: "err", text: typeof detail === "string" ? detail : "Rate limit exceeded. Please try again in a few minutes." });
            } else if (err?.response?.status === 422 || err?.response?.status === 400) {
                setStatus({ kind: "err", text: typeof detail === "string" ? detail : "Please double-check your details and try again." });
            } else {
                setStatus({ kind: "err", text: "Delivery failed. Please try again shortly." });
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className={`rounded-lg border p-6 md:p-8 ${cardBg}`} data-testid="contact-form-card">
            <div className="overline mb-2" style={{ color: BRAND_RED }} data-testid="contact-form-eyebrow">
                Send a message
            </div>
            <h2 className="text-h1 md:text-[40px] font-inter font-bold tracking-tight leading-tight mb-2">
                Talk to the desk directly.
            </h2>
            <p className={`text-body leading-relaxed mb-6 ${subtleText}`}>
                For account, billing, or payout questions. Our operator team replies within one business day.
            </p>

            <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4" data-testid="contact-form">
                {/* Honeypot — invisible to humans, filled only by bots */}
                <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", opacity: 0 }}>
                    <input
                        ref={honeypotRef}
                        type="text"
                        name="md_username_verification_honey"
                        tabIndex={-1}
                        autoComplete="off"
                        data-testid="contact-form-honeypot"
                    />
                </div>

                <Field label="Full name" name="name" type="text" required placeholder="Kamau Njoroge"
                       faintText={faintText} testid="contact-form-name" />
                <Field label="Email address" name="email" type="email" required placeholder="you@example.com"
                       faintText={faintText} testid="contact-form-email" />

                <label className="flex flex-col gap-1">
                    <span className="text-caption uppercase tracking-widest font-heading font-bold text-slate-600 dark:text-slate-300">Phone Number</span>
                    <PhoneCountryInput
                        dialCode={dialCode}
                        nationalNumber={nationalNumber}
                        onDialChange={setDialCode}
                        onNumberChange={(v) => { setNationalNumber(v); setPhoneTouched(true); }}
                        testidPrefix="contact-form-phone"
                        touched={phoneTouched}
                    />
                </label>

                <label className="flex flex-col gap-1">
                    <span className="text-caption uppercase tracking-widest font-heading font-bold text-slate-600 dark:text-slate-300">Message</span>
                    <textarea
                        name="message"
                        required
                        rows={5}
                        placeholder="Give us the context — trade, subscription, payout, other."
                        className="rounded-lg border border-border/70 bg-transparent px-3 py-2.5 text-[16px] leading-relaxed outline-none focus-visible:ring-2 focus-visible:ring-[#006600] dark:focus-visible:ring-[#4ADE80] transition-colors resize-y"
                        data-testid="contact-form-message"
                    />
                </label>

                {/* Image attachment — max 2MB, jpeg/png/webp only */}
                <label className="flex flex-col gap-1">
                    <span className="text-caption uppercase tracking-widest font-heading font-bold text-slate-600 dark:text-slate-300">
                        Attach an image <span className="opacity-60">(optional · max 2MB · JPEG, PNG, or WEBP)</span>
                    </span>
                    {!attachment ? (
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="min-h-[44px] inline-flex items-center gap-2 rounded-lg border border-dashed border-border/70 px-3 py-2.5 text-sm text-muted-foreground hover:border-[#006600] transition-colors w-fit"
                            data-testid="contact-form-attachment-trigger"
                        >
                            <Paperclip className="w-4 h-4" /> Choose an image
                        </button>
                    ) : (
                        <div className="flex items-center gap-2 rounded-lg border border-border/70 px-3 py-2 text-sm w-fit" data-testid="contact-form-attachment-preview">
                            <img src={attachmentPreviewUrl} alt="Attachment preview" className="w-9 h-9 rounded object-cover shrink-0" data-testid="contact-form-attachment-thumbnail" />
                            <span className="truncate max-w-[180px]">{attachment.name}</span>
                            <button
                                type="button"
                                onClick={removeAttachment}
                                aria-label="Remove attachment"
                                className="min-h-[24px] min-w-[24px] flex items-center justify-center text-muted-foreground hover:text-destructive"
                                data-testid="contact-form-attachment-remove"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={onAttachmentChange}
                        className="hidden"
                        data-testid="contact-form-attachment-input"
                    />
                    {attachmentError && (
                        <p className="text-xs text-destructive mt-1" data-testid="contact-form-attachment-error">{attachmentError}</p>
                    )}
                </label>

                <button
                    type="submit"
                    disabled={busy}
                    className="text-white font-extrabold px-6 py-3 min-h-[44px] rounded-lg text-sm shadow-lg transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ background: BRAND_SOLID }}
                    onMouseEnter={(e) => !busy && (e.currentTarget.style.background = BRAND_HOVER)}
                    onMouseLeave={(e) => !busy && (e.currentTarget.style.background = BRAND)}
                    data-testid="contact-form-submit"
                >
                    {busy ? "Sending…" : "Send message"}
                </button>

                {status && (
                    <div
                        className="text-sm font-semibold rounded-lg px-4 py-3 leading-relaxed"
                        style={
                            status.kind === "ok"
                                ? { background: BRAND_10, color: BRAND }
                                : { background: "rgba(220,38,38,0.08)", color: "#B91C1C" }
                        }
                        data-testid={`contact-form-status-${status.kind}`}
                        role={status.kind === "err" ? "alert" : "status"}
                    >
                        {status.text}
                    </div>
                )}
            </form>
        </div>
    );
}

function Field({ label, name, type, required, placeholder, faintText, testid }) {
    return (
        <label className="flex flex-col gap-1">
            <span className="text-caption uppercase tracking-widest font-heading font-bold text-slate-600 dark:text-slate-300">{label}</span>
            <input
                type={type}
                name={name}
                required={required}
                placeholder={placeholder}
                className="rounded-lg border border-border/70 bg-transparent px-3 py-2.5 h-11 text-[16px] outline-none focus-visible:ring-2 focus-visible:ring-[#006600] dark:focus-visible:ring-[#4ADE80] transition-colors"
                data-testid={testid}
            />
        </label>
    );
}
